import {Resource, ResourceLoadContext } from "./resource";
import { TextureResource } from "./textureresource";
import MeshResource from "./mesh_resource";
import { SoundResource}  from "./soundresource";
import ShaderService from "../shader/shader_service";
import Stage from "../stage/stage";
import {assetCache} from "./asset-cache";
import {loadingState} from "./loading-state";

export default class ResourceService 
{
  private ctx?: ResourceLoadContext;
  private readonly loaded = new Map<string, Resource<unknown>>();
  private readonly inflight = new Map<string, Promise<Resource<unknown>>>();

  constructor() {}

  createContext(device: GPUDevice): void {
    this.ctx = {
      device,

      async fetchPrivate(path: string): Promise<Response> {
        return assetCache.fetch("resource",path);
      },
    };
  }

  async getTexture(path: string): Promise<TextureResource> {
    const ctx = this.requireContext();
    const cacheKey = `texture:${path}`;
    return this.getOrLoad(ctx, cacheKey, () => new TextureResource(path));
  }
  /** Small declarative resources use the same local/cloud asset route as sound. */
  async getJSON(path:string):Promise<unknown>{
    return (await this.requireContext().fetchPrivate(path)).json();
  }
    async getSound(path: string): Promise<SoundResource> {
    const ctx = this.requireContext();
    const cacheKey = `sound:${path}`;
    return this.getOrLoad(ctx, cacheKey, () => new SoundResource(path));
  }

  async getMesh(path: string): Promise<MeshResource> {
    const ctx = this.requireContext();
    const cacheKey = `mesh:${path}`;
    return this.getOrLoad(ctx, cacheKey, () => new MeshResource(path));
}

  disposeAll(): void {
    const ctx = this.requireContext();

    for (const resource of this.loaded.values()) {
      resource.dispose(ctx);
    }

    this.loaded.clear();
    this.inflight.clear();
  }

  private requireContext(): ResourceLoadContext 
  {
    if (!this.ctx)
    {
      const device= Stage.instance().shader_service.get_device();
      if(device)
      {
        this.createContext(device);
        if(this.ctx)
        {
          return this.ctx;
        }
      }
         throw new Error("ResourceService has not been initialized. Call createContext(device) first.");
    }
    return this.ctx;
  }

  private async getOrLoad<TResource extends Resource<unknown>>(
    ctx: ResourceLoadContext,
    cacheKey: string,
    factory: () => TResource
  ): Promise<TResource> {
    const existing = this.loaded.get(cacheKey);
    if (existing) {
      return existing as TResource;
    }

    const pending = this.inflight.get(cacheKey);
    if (pending) {
      return pending as Promise<TResource>;
    }

    const resource = factory();

    const promise = resource.load(ctx)
      .then(() => {
        loadingState.activity(cacheKey.slice(cacheKey.indexOf(":")+1));
        this.loaded.set(cacheKey, resource);
        this.inflight.delete(cacheKey);
        return resource;
      })
      .catch((error) => {
        this.inflight.delete(cacheKey);
        throw error;
      });

    this.inflight.set(cacheKey, promise);
    return promise as Promise<TResource>;
  }
}
